import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

import type { Fact } from '@/domains/types'
import { FactTile } from './FactTile'

interface SortableFactTileProps {
  fact: Fact
  leadId: string
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
  onPublish?: () => void
  onUnpublish?: () => void
  onResetVotes?: () => void
}

/**
 * Drag-and-drop-aware wrapper around `FactTile`: owns the dnd-kit
 * `useSortable` wiring and builds the drag handle, keeping `FactTile`
 * itself free of any dnd-kit dependency. Only the handle carries the
 * pointer/keyboard listeners so existing tile buttons (edit/delete/
 * publish/score) keep working without interference from dragging.
 */
export function SortableFactTile({ fact, ...tileProps }: SortableFactTileProps) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fact.id })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={t('lead_detail.fact_drag_handle')}
      className="cursor-grab touch-none p-1.5 text-muted-foreground hover:text-foreground active:cursor-grabbing transition-colors"
    >
      <GripVertical className="w-4 h-4" />
    </button>
  )

  return (
    <div ref={setNodeRef} style={style}>
      <FactTile fact={fact} {...tileProps} dragHandle={dragHandle} />
    </div>
  )
}
